import { Fragment, type ReactNode } from "react";

import type { TextComposerPreviewContent } from "./useMessagePreview";

const URL_TOKEN_RE = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;
const PREVIEW_URL_TOKEN_RE = /^(https?:\/\/[^\s]+|www\.[^\s]+)$/i;

const SHORT_LINK_ORIGIN = "https://qn.one";

export function stripUrls(text: string): string {
  return text.replace(URL_TOKEN_RE, "");
}

export function shortenPreviewUrl(url: string): string {
  let hash = 0;
  for (let i = 0; i < url.length; i++) {
    hash = (hash * 31 + url.charCodeAt(i)) | 0;
  }
  const id = Math.abs(hash).toString(36).padStart(6, "0").slice(0, 6);
  return `${SHORT_LINK_ORIGIN}/${id}`;
}

/** Splits text into plain parts and `.preview-url` links; `asLinks: false` renders spans for use inside an existing link. */
export function renderMessageWithUrls(
  text: string,
  shortenUrls = false,
  asLinks = true,
): ReactNode {
  if (!text) return null;
  const parts = text.split(URL_TOKEN_RE);
  return parts.map((part, i) => {
    if (!part) return null;
    if (PREVIEW_URL_TOKEN_RE.test(part)) {
      const href = /^https?:\/\//i.test(part) ? part : `https://${part}`;
      const label = shortenUrls ? shortenPreviewUrl(href) : part;
      if (!asLinks) {
        return (
          <span key={i} className="preview-url">
            {label}
          </span>
        );
      }
      return (
        <a
          key={i}
          className="preview-url"
          href={href}
          target="_blank"
          rel="noopener noreferrer"
        >
          {label}
        </a>
      );
    }
    return <Fragment key={i}>{part}</Fragment>;
  });
}

/**
 * True when server- or frame-derived preview content has visible body text or an image URL.
 * Used for read-only `IosPhone` (`readOnlyPreviewIsEmpty`) anywhere the mockup is not tied to the composer editor.
 */
export function previewHasRenderableBody(
  preview: TextComposerPreviewContent | null | undefined,
): boolean {
  if (!preview?.body) return false;
  const { body } = preview;
  if (body.type === "IMAGE" || body.type === "DOCUMENT") {
    return typeof body.url === "string" && body.url.trim().length > 0;
  }
  return typeof body.text === "string" && body.text.trim().length > 0;
}

/**
 * Rendered body text of a server/frame preview, used to base the character
 * count on what actually gets sent (merge tags/offers resolved, brand name
 * prepended). Returns `undefined` for IMAGE-only or empty bodies so callers
 * fall back to the authoring text.
 */
export function extractPreviewBodyText(
  preview: TextComposerPreviewContent | null | undefined,
): string | undefined {
  const text = preview?.body?.text;
  return typeof text === "string" && text.length > 0 ? text : undefined;
}

/**
 * Extracts the rendered `transformed.content` block from a frame variant's
 * content_json, used as the read-only preview fallback when no server preview
 * is available. Returns null when the shape doesn't contain it.
 */
export function getTransformedContentFromContentJson(
  contentJson: unknown,
): TextComposerPreviewContent | null {
  if (!contentJson || typeof contentJson !== "object") {
    return null;
  }

  const transformed = (contentJson as Record<string, unknown>).transformed;
  if (!transformed || typeof transformed !== "object") {
    return null;
  }

  const content = (transformed as Record<string, unknown>).content;
  if (!content || typeof content !== "object") {
    return null;
  }

  return content as TextComposerPreviewContent;
}
